import React from 'react';
import { Link } from 'react-router-dom';
import '../Article.css';
import llmArchitecture from './Resources/LLM Architecture.png';

const Shape = ({ children }) => <span className="shape">{children}</span>;

const Formula = ({ label, children }) => (
  <div className="formula">
    {label && <span className="label">{label}</span>}
    {children}
  </div>
);

const Eq = ({ children }) => <div className="eq">{children}</div>;

const Step = ({ n, title, input, output, children }) => (
  <div className="step">
    <div className="step-num">{n}</div>
    <div>
      <h5>{title}</h5>
      {children}
      <div className="io">
        <Shape>{input}</Shape>
        <span>→</span>
        <Shape>{output}</Shape>
      </div>
    </div>
  </div>
);

const toc = [
  ['big-picture', 'The big picture'],
  // ['notation', 'Notation & running example'],
  ['tokens-to-vectors', 'Tokens to vectors'],
  ['dropout-layer', 'Dropout Layer'],
  ['shortcut-connection', 'Shortcut Connection'],
  ['layer-norm', 'Layer Normalization'],
  ['attention', 'Multihead Attention'],
  ['ffn', 'Feed-forward network'],
  ['output', 'Linear output Layer'],
  // ['takeaways', 'Key takeaways'],
];

export default function ArchitectureOfModernLLMs({ post }) {
  return (
    <div className="article-page">
      <Link to="/blog" className="article-back">← Back to Articles</Link>

      {/* Hero */}
      <header className="article-hero">
        <span className="article-kicker">{post.category} · Deep Dive</span>
        <h1>{post.title} <span style={{ fontSize: "0.5em", fontWeight: "normal"}}>(In simple words)</span></h1>
        <p className="lead">
          Every frontier language model — GPT, Llama, Mistral, DeepSeek, Qwen — is built on the same core recipe: a
          stack of decoder-only Transformer blocks. This article walks you through stage by stage.
        </p>
        <div className="article-meta">
          {/* <span><b>{post.author}</b></span> */}
          <span>{post.date}</span>
          <span>{post.readTime}</span>
        </div>
        <div className="article-tags">
          {post.tags.map((t) => <span key={t}>{t}</span>)}
        </div>
      </header>

      <div className="article-layout">
        <aside className="article-toc">
          <h4>Contents</h4>
          <ol>
            {toc.map(([id, label]) => (
              // <li key={id}><a href={`#${id}`}>{label}</a></li>
              <li><a href={`#${id}`}>{label}</a></li>
            ))}
          </ol>
        </aside>

        <article className="article-body">
          {/* Big picture */}
          <section id="big-picture">
            <h2>1. The big picture</h2>
            <p>
              At its core, a modern LLM does one simple thing: <b>it predicts the next token</b>. A token is a small
              piece of text, usually a word, part of a word or a punctuation mark. The model reads the tokens it
              has so far, x<sub>1</sub>, x<sub>2</sub>, …, x<sub>t</sub>, and gives a probability to every token in
              its vocabulary for being the next token, x<sub>t+1</sub>. This is called <b>autoregressive </b>
              generation: each new token is predicted from all the tokens before it, including the ones the model
              itself just generated.
            </p>
            <p>Throughout this article we will use the following sentence as our running example:</p>
            <div className="callout">
              <p style={{ fontSize: 18, fontWeight: 600, color: '#1E2F3D' }}>
                “The dog chased the cat, and then the cat chased the dog”
              </p>
            </div>
            <p>
              For simplicity, assume every word and the comma is one token. The sentence then has 13 tokens:
            </p>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Position t</th><th>1</th><th>2</th><th>3</th><th>4</th><th>5</th><th>6</th><th>7</th><th>8</th><th>9</th><th>10</th><th>11</th><th>12</th><th>13</th></tr>
                </thead>
                <tbody>
                  <tr><td><b>Token x<sub>t</sub></b></td><td>The</td><td>dog</td><td>chased</td><td>the</td><td>cat</td><td>,</td><td>and</td><td>then</td><td>the</td><td>cat</td><td>chased</td><td>the</td><td>dog</td></tr>
                </tbody>
              </table>
            </div>

            <h3>How the (t+1)-th token is generated</h3>
            <p>
              Suppose the user’s prompt is the first 8 tokens, <i>“The dog chased the cat, and then”</i>, so t = 8.
              The model produces the rest of the sentence one token at a time. At each step it runs the whole
              network on the tokens so far, gets probabilities for x<sub>t+1</sub>, picks a token, appends it to
              the input and repeats with t + 1:
            </p>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Step</th><th>Input tokens x<sub>1</sub> … x<sub>t</sub></th><th>t</th><th>Predicted x<sub>t+1</sub></th></tr>
                </thead>
                <tbody>
                  <tr><td>1</td><td>The dog chased the cat, and then</td><td>8</td><td><b>the</b></td></tr>
                  <tr><td>2</td><td>The dog chased the cat, and then the</td><td>9</td><td><b>cat</b></td></tr>
                  <tr><td>3</td><td>The dog chased the cat, and then the cat</td><td>10</td><td><b>chased</b></td></tr>
                  <tr><td>4</td><td>The dog chased the cat, and then the cat chased</td><td>11</td><td><b>the</b></td></tr>
                  <tr><td>5</td><td>The dog chased the cat, and then the cat chased the</td><td>12</td><td><b>dog</b></td></tr>
                </tbody>
              </table>
            </div>
            <p>
              The model does not output just one word; it outputs a probability for every token
              in the vocabulary. For example (illustrative numbers):
            </p>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Candidate x<sub>13</sub></th><th>dog</th><th>mouse</th><th>ball</th><th>cat</th><th>… all other tokens</th></tr>
                </thead>
                <tbody>
                  <tr><td><b>Probability</b></td><td>0.71</td><td>0.09</td><td>0.04</td><td>0.02</td><td>0.14 in total</td></tr>
                </tbody>
              </table>
            </div>
            <p>
              The token is then chosen from this distribution, either the most likely one or by sampling. Choosing
              “dog” makes sense because of context the model saw much earlier: in “The dog chased the cat” the roles
              were dog → cat, and “and then the cat chased the …” signals that the roles are reversed. Linking a
              token to relevant earlier tokens like this is exactly the job of the <b>attention</b> layers
              (Section 5).
            </p>
            <p>
              During <b>training</b> the full sentence is known, so the model learns all of these predictions in one
              pass: from “The” it predicts “dog”, from “The dog” it predicts “chased”, and so on up to position 12.</p>
            <p>
              Architecturally, almost all of today’s models are <b>decoder-only Transformers</b>. The input flows
              through four phases: tokenisation, embedding, a deep stack of identical decoder blocks, and an output
              head that turns the final hidden state back into vocabulary probabilities.
            </p>

            <figure className="article-figure">
              <img src={llmArchitecture} alt="Architecture of a modern LLM: tokenised text, token and positional embeddings, a transformer block with layer normalisation, multi-head attention, feed-forward/MoE layer, dropout and residual connections, followed by final layer normalisation and a linear output layer" />
              <figcaption>Architecture of a modern LLM, using our example: the input “The dog chased the cat, and then” is extended by one token to “… and then the”.</figcaption>
            </figure>

            <div>
              <p>
                Let's discuss each layer one by one on high level.
              </p>
            </div>
          </section>

          {/* Notation */}
          {/* <section id="notation">
            <h2>Notation &amp; running example</h2>
            <p>
              To make the shapes concrete we use a configuration modelled on <b>Llama-3-8B</b> (dense) and
              <b> Mixtral-8×7B</b> (MoE), which share the same hidden size and FFN width.
            </p>
            
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Symbol</th><th>Meaning</th><th>Example value</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>B</code></td><td>Batch size (number of sequences)</td><td>1</td></tr>
                  <tr><td><code>T</code></td><td>Sequence length (tokens in context)</td><td>4,096</td></tr>
                  <tr><td><code>V</code></td><td>Vocabulary size</td><td>128,256</td></tr>
                  <tr><td><code>d</code></td><td>Model / hidden dimension (d<sub>model</sub>)</td><td>4,096</td></tr>
                  <tr><td><code>L</code></td><td>Number of decoder blocks (layers)</td><td>32</td></tr>
                  <tr><td><code>h</code></td><td>Number of query heads</td><td>32</td></tr>
                  <tr><td><code>h<sub>kv</sub></code></td><td>Number of key/value heads (GQA)</td><td>8</td></tr>
                  <tr><td><code>d<sub>h</sub></code></td><td>Head dimension = d / h</td><td>128</td></tr>
                  <tr><td><code>d<sub>ff</sub></code></td><td>FFN intermediate dimension</td><td>14,336</td></tr>
                  <tr><td><code>E</code></td><td>Number of experts (MoE)</td><td>8</td></tr>
                  <tr><td><code>k</code></td><td>Experts activated per token (top-k)</td><td>2</td></tr>
                </tbody>
              </table>
            </div>
            <p>
              Shapes are written as <Shape>[B, T, d]</Shape>, meaning a 3-D array with batch, sequence and feature
              axes. With our example values, <Shape>[B, T, d]</Shape> = <Shape>[1, 4096, 4096]</Shape>.
            </p>
          </section> */}

          {/* 1. Tokens to vectors */}
          <section id="tokens-to-vectors">
            <h2>2. Tokens to vectors</h2>
            <p>
              <b> Tokenised Text - </b>
              Neural networks operate on numbers, not characters.
              
              Suppose our embadding vector size is d=4096. In this step the sentence will be converted to tokens. Common
              words become one token; rare words split into several pieces. In our case, for simplicity, let's assume the sentence 
              "The dog chased the cat, and then the cat chased the dog" is converted to tokens like like 'The', 'dog', 'chased', 
              'the', 'cat' etc. 
              </p>
              <p>
              <b>Token Embedding - </b>After converting to token, every token will be converted to a d dimentional embedding vector, as shown below.
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Token</th><th>Token ID (row)</th><th>Embedding vector (d = 4,096 numbers)</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>"The"</td><td>e.g. 791</td><td>[ 0.012, −0.334, 0.087, …, 0.251 ]</td></tr>
                    <tr><td>"dog"</td><td>e.g. 5,679</td><td>[ −0.145, 0.072, 0.410, …, −0.018 ]</td></tr>
                    <tr><td>"chased"</td><td>e.g. 62,872</td><td>[ 0.293, 0.006, −0.221, …, 0.134 ]</td></tr>
                    <tr><td>"cat"</td><td>e.g. 8,415</td><td>[ −0.118, 0.091, 0.377, …, 0.042 ]</td></tr>
                  </tbody>
                </table>
              </div>

              <div>
                <b>Positional Encoding - </b> The next step is <b>positional encoding</b>. Till this point similar tokens like 'the', 'cat', 'dog' etc 
                have same embedding vector but there meaning in the sentence is not same if they are at different position 
                in the sentence. To add positional context in the embedding vector of tokens a trainable 
                (the vector which will be trained during training process) positional vector is added to embedding vector to 
                add positional information in the token. This is called <b>Positional encoding</b>.
              </div>
              <div>
                <p>
                  <br/>
                After <b>positional embedding layer</b> vector of every token will be different. Even the two same tokens of "dog" 
                will now be having two different vectors.
                </p>
              </div>
            </p>
           
          </section>
          <section id="dropout-layer">
            <h2>3. Dropout Layer</h2>
            <p>
              To make the neural network generic and resolve the issue of over
                 fitting. This is the common practice to drop some neurons in the network ramdomly 
                 so that the result will be generic.<br/>
              <br/>
              Dropout layers are active only during training and are deactivated during inference. In this process 
              few neurons are forced not to participate in training ramdomly. For example, with a dropout rate of 
              p = 0.1, about 10% of the neurons will be forced to switched off ramdomly. It is needed because a 
              large network can otherwise rely too heavily on a few specific neurons
              and simply memorise its training text (overfitting), which makes it perform poorly on new sentences.
              Since any neuron can disappear at any step, the network is forced to spread what it learns across many
              neurons, which makes it more robust and better at generalising. 
            </p>
          </section>

          <section id="shortcut-connection">
            <h2>4. Shortcut Connection</h2>
            <p>
              A shortcut connection (also called a residual or skip connection) adds a layer’s input directly to its
              output, so each block computes x + Layer(x) instead of just Layer(x). This is needed because LLMs stack
              dozens of layers, and during training the gradient signal has to travel backwards through all of them;
              without a shortcut it gets multiplied many times and can shrink to almost zero (the vanishing gradient
              problem), so the early layers stop learning. The shortcut gives the gradient a direct path back to every
              layer, which keeps training stable even for very deep models. It also means each layer only has to learn
              a small change on top of what it receives, rather than rebuilding the whole representation, while the
              original token information is carried forward unchanged in shape.
            </p>
          </section>

          <section id="layer-norm">
            <h2>5. Layer Normalization</h2>
            <p>
              As token vectors pass through many layers, their values can drift and become very large or very small,
              which makes training unstable and slow. Layer normalization fixes this by bringing each token’s vector
              back to a consistent scale before it enters the next sub-layer, such as attention or the feed-forward
              network. It works on each token separately: for the d = 4,096 values of a token, it computes their mean
              μ and variance σ², and replaces every value x<sub>i</sub> with (x<sub>i</sub> − μ) / √(σ² + ε), where ε
              is a tiny number that avoids division by zero. Two learned vectors, a scale γ and a shift β, are then
              applied (y<sub>i</sub> = γ<sub>i</sub> · x̂<sub>i</sub> + β<sub>i</sub>) so the model can still choose
              the best range for each value, and the shape stays unchanged. Most modern
              LLMs use a simpler variant called RMSNorm, which skips the mean and β and only divides by the root mean
              square √((1/d) Σ x<sub>i</sub>² + ε).
            </p>
          </section>

           {/* 4. Attention */}
          <section id="attention">
            <h2>6. Multihead Attention</h2>
            <p>
              Self-attention is the only place where tokens exchange information. For each position, the model asks:
              <i> “Which earlier tokens are relevant to me, and what should I take from them?”</i> Each token emits
              three vectors:
            </p>
            <ul>
              <li><b>Query (Q)</b> — what this token is looking for.</li>
              <li><b>Key (K)</b> — what this token offers to be matched against.</li>
              <li><b>Value (V)</b> — the content this token passes on if attended to.</li>
            </ul>
            <p>
              Attention compares every query with every key, turns the similarities into weights with a softmax, and
              returns a weighted average of values. Doing this with <b>h</b> independent, smaller heads lets the
              model track several relationships at once — one head may follow syntax, another co-reference, another
              copy patterns.
            </p>

            <Formula label="Scaled dot-product attention">
              <Eq>Attention(Q, K, V) = softmax( Q K<sup>T</sup> / √d<sub>h</sub> + M ) V</Eq>
            </Formula>
          </section>

          {/* 5. FFN */}
          <section id="ffn">
            <h2>7. Feed-forward network</h2>
            <p>
              After attention has gathered context, the <b>feed-forward network (FFN)</b>, also called the MLP,
              processes each token independently. It expands the vector into a wider space, applies a non-linearity,
              and projects back. Interpretability research suggests FFN layers act like a large key-value memory:
              this is where much of the model’s factual knowledge is stored. FFNs hold roughly <b>two-thirds of the
              parameters</b> in a dense LLM.
            </p>

            <h3>Classic FFN</h3>
            <Formula label="Original Transformer FFN">
              <Eq>FFN(x) = W<sub>2</sub> · σ( W<sub>1</sub> x + b<sub>1</sub> ) + b<sub>2</sub></Eq>
            </Formula>
            <p>
              With σ = ReLU or GELU, W<sub>1</sub> ∈ ℝ<sup>d × d<sub>ff</sub></sup>, W<sub>2</sub> ∈
              ℝ<sup>d<sub>ff</sub> × d</sup> and typically d<sub>ff</sub> = 4d.
            </p>

            <h3>SwiGLU — the modern FFN</h3>
            <p>
              Llama, Mistral, Qwen, DeepSeek and most current models use a <b>gated</b> FFN called SwiGLU. It has
              three matrices instead of two: a gate projection, an up projection and a down projection. The gate
              learns, per dimension, how much of the up-projected signal to let through. Biases are dropped.
            </p>

            <p>
              <b>Mixture of Experts (MoE)</b> - Instead of using a single dense FFN, parallel FFNs (the experts) and a small
              <b> router</b> that sends each token to only <b>k</b> of them is used. It helps in reducing the 
              calculation size and also makes the responsible network expert with the knowledge of that area. 

            </p>
            <p>
              Mixtral-8×7B, DeepSeek-V3, Qwen3-MoE, Llama 4 and (reportedly) several frontier closed models are MoE.
              Attention, embeddings and norms stay dense and shared; only the FFN is replaced.
            </p>
          </section>

          {/* 7. Output */}
          <section id="output">
            <h2>8. Linear output Layer</h2>
            <p>
              After the last transformer block, every token still has a vector of d = 4,096 numbers. These numbers
              carry meaning, but they are not words yet. The linear output layer turns the vector of the
              <b> last token</b> into a score for every token in the vocabulary, and the highest-scoring token becomes
              the predicted next word. 
              {/* Let’s follow our example, where the input is
              <i> “The dog chased the cat, and then the cat chased the”</i> (12 tokens) and the model has to predict
              token 13. */}
            </p>
            {/* <div className="steps">
              <Step n="1" title="Final layer normalization" input="[1, 12, 4096]" output="[1, 12, 4096]">
                <p>
                  The output of the last transformer block is normalized one more time, so all values are on a
                  consistent scale before scoring.
                </p>
              </Step>
              <Step n="2" title="Take the vector of the last token" input="[1, 12, 4096]" output="[1, 4096]">
                <p>
                  To predict the next word we only need the vector at the last position, the one for “the” at
                  position 12. Thanks to attention, this vector already contains information from all the earlier
                  tokens, including the fact that the dog and the cat have swapped roles.
                </p>
              </Step>
              <Step n="3" title="Linear layer: vector → one score per vocabulary token" input="[1, 4096]" output="[1, 128256]">
                <p>
                  The vector is multiplied by a learned weight matrix W<sub>U</sub> with 4,096 rows and V = 128,256
                  columns, one column per token in the vocabulary. Each output number, called a <b>logit</b>, is a
                  score for how well that token fits as the next word.
                </p>
                <Formula>
                  <Eq>z = h W<sub>U</sub>, &nbsp; W<sub>U</sub> ∈ ℝ<sup>4096 × 128256</sup></Eq>
                </Formula>
              </Step>
              <Step n="4" title="Softmax: scores → probabilities" input="[1, 128256]" output="[1, 128256]">
                <p>
                  Softmax turns the logits into probabilities that are all positive and add up to 1. The temperature τ
                  controls how confident the choice is: a lower τ favours the top token, a higher τ makes the output
                  more varied.
                </p>
                <Formula>
                  <Eq>p<sub>i</sub> = exp(z<sub>i</sub> / τ) / Σ<sub>j=1…V</sub> exp(z<sub>j</sub> / τ)</Eq>
                </Formula>
              </Step>
              <Step n="5" title="Pick a token ID and convert it back to text" input="[1, 128256]" output="1 token ID → “dog”">
                <p>
                  The model picks a token, either the most likely one or by sampling from the probabilities. The
                  chosen token ID is looked up in the tokenizer’s vocabulary to get the text, here “dog”.
                </p>
              </Step>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Candidate token</th><th>Logit z (score)</th><th>Probability p</th></tr>
                </thead>
                <tbody>
                  <tr><td><b>dog</b></td><td>9.1</td><td><b>0.71</b></td></tr>
                  <tr><td>mouse</td><td>7.0</td><td>0.09</td></tr>
                  <tr><td>ball</td><td>6.2</td><td>0.04</td></tr>
                  <tr><td>cat</td><td>5.5</td><td>0.02</td></tr>
                  <tr><td>… the other 128,252 tokens</td><td>much lower</td><td>0.14 in total</td></tr>
                </tbody>
              </table>
            </div>
            <p>
              (Numbers are illustrative.) “dog” is appended to the input, giving 13 tokens, and the whole process
              runs again to predict token 14. Repeating this loop one token at a time is how the model writes a full
              answer.
            </p> */}
          </section>

          {/* 8. Shapes summary */}
          {/* <section id="shapes">
            <h2>8. End-to-end shape summary</h2>
            <p>All shapes for one forward pass with B = 1, T = 4,096 using the Llama-3-8B-style configuration.</p>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Stage</th><th>Key formula</th><th>Output shape</th><th>Example</th></tr>
                </thead>
                <tbody>
                  <tr><td>Token IDs</td><td>tokenizer(text)</td><td><code>[B, T]</code></td><td>[1, 4096]</td></tr>
                  <tr><td>Embedding</td><td>W<sub>E</sub>[x]</td><td><code>[B, T, d]</code></td><td>[1, 4096, 4096]</td></tr>
                  <tr><td>RMSNorm</td><td>x / rms(x) ⊙ γ</td><td><code>[B, T, d]</code></td><td>[1, 4096, 4096]</td></tr>
                  <tr><td>Q projection + split</td><td>X̂ W<sub>Q</sub></td><td><code>[B, h, T, dₕ]</code></td><td>[1, 32, 4096, 128]</td></tr>
                  <tr><td>K, V projection + split</td><td>X̂ W<sub>K</sub>, X̂ W<sub>V</sub></td><td><code>[B, hₖᵥ, T, dₕ]</code></td><td>[1, 8, 4096, 128]</td></tr>
                  <tr><td>Attention scores</td><td>QK<sup>T</sup>/√d<sub>h</sub> + M</td><td><code>[B, h, T, T]</code></td><td>[1, 32, 4096, 4096]</td></tr>
                  <tr><td>Weighted values</td><td>softmax(S) V</td><td><code>[B, h, T, dₕ]</code></td><td>[1, 32, 4096, 128]</td></tr>
                  <tr><td>Concat + W<sub>O</sub></td><td>Concat(heads) W<sub>O</sub></td><td><code>[B, T, d]</code></td><td>[1, 4096, 4096]</td></tr>
                  <tr><td>FFN gate / up</td><td>x W<sub>gate</sub>, x W<sub>up</sub></td><td><code>[B, T, d_ff]</code></td><td>[1, 4096, 14336]</td></tr>
                  <tr><td>FFN down</td><td>(SiLU(G) ⊙ U) W<sub>down</sub></td><td><code>[B, T, d]</code></td><td>[1, 4096, 4096]</td></tr>
                  <tr><td>MoE router</td><td>x W<sub>r</sub></td><td><code>[N, E]</code></td><td>[4096, 8]</td></tr>
                  <tr><td>MoE top-k gates</td><td>softmax(TopK(r))</td><td><code>[N, k]</code></td><td>[4096, 2]</td></tr>
                  <tr><td>MoE combine</td><td>Σ g<sub>i</sub> Expert<sub>i</sub>(x)</td><td><code>[B, T, d]</code></td><td>[1, 4096, 4096]</td></tr>
                  <tr><td>Logits</td><td>h W<sub>U</sub></td><td><code>[B, T, V]</code></td><td>[1, 4096, 128256]</td></tr>
                  <tr><td>Next-token probs</td><td>softmax(z<sub>T</sub> / τ)</td><td><code>[B, V]</code></td><td>[1, 128256]</td></tr>
                </tbody>
              </table>
            </div>

            <div className="callout">
              <h4>Rule of thumb for compute</h4>
              <p>
                A forward pass costs about <b>2 FLOPs per active parameter per token</b> (one multiply and one
                add), plus the attention term that grows with context length. Training (forward + backward) costs
                about <b>6 · N<sub>active</sub> · tokens</b>. This is why MoE is attractive: compute scales with
                active, not total, parameters.
              </p>
            </div>
          </section>

          {/* Takeaways */}
          {/* <section id="takeaways">
            <h2>Key takeaways</h2>
            <ul>
              <li>Modern LLMs are stacks of identical <b>pre-norm decoder blocks</b> around a residual stream of fixed shape <Shape>[B, T, d]</Shape>.</li>
              <li><b>Attention</b> mixes information across positions via softmax(QK<sup>T</sup>/√d<sub>h</sub>)V. Its <Shape>[T, T]</Shape> score matrix makes it quadratic in context length; GQA, RoPE, FlashAttention and the KV cache make it practical.</li>
              <li>The <b>FFN</b> (now SwiGLU) transforms each token independently, expanding to d<sub>ff</sub> and back, and holds most of a dense model’s parameters.</li>
              <li><b>Mixture of Experts</b> replaces the FFN with many experts plus a router, decoupling total capacity from per-token compute — at the cost of memory, communication and load-balancing complexity.</li>
              <li>Tracking tensor shapes stage by stage is the fastest way to understand — and debug — any Transformer implementation.</li>
            </ul>
          </section> */}
        </article>
      </div>
    </div>
  );
} 
